import { FC, MouseEventHandler, useCallback, useMemo, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import cn from 'classnames';

import {
  PickDtoFilter,
  PickDtoIsRequired,
  PickDtoOnChange,
} from '@/shared/lib/forms/form-wrapper.types';
import { CollectionName, useSJDatabase } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { Select } from '@/shared/uikitV2/Select';
import { FieldSelectView } from '@/shared/uikitV2/Select/FieldSelectView';
import { useModalsStore } from '@/widgets/modals';
import { AllowedSelectEntityName } from '@/widgets/modals/containers/SelectEntitiesModal/types';

import { commonFilterCollection, getPickArrayItemCategory } from '../helpers';

type PickArrayDtoItem = {
  id: string;
  name?: string;
  nickname?: string;
  nickName?: string;
  shortname?: string;
  type?: string;
  category?: string;
};

type PickArrayOfDtoProps = {
  label: string;
  required?: boolean | string;
  collections: AllowedSelectEntityName[];
  createEntityTypes?: string[];
  createEntityCategories?: string[];
  fieldName: string;
  disabled?: boolean;
  multiple?: boolean;
  excludeIds?: DtoId[];
  filter?: PickDtoFilter;
  isRequired?: PickDtoIsRequired;
  onValueChange?: PickDtoOnChange;
  onDelete?: (fieldName: string) => void;
  isHighlight?: boolean;
};

export const ALLOWED_PICK_ARRAY_ENTITIES = [
  CollectionName.ASSETS,
  CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
  CollectionName.PRIVATE_DOCUMENTS,
  CollectionName.CONTACTS,
] as const;

const PickArrayItemWithCategory = [CollectionName.ASSETS, CollectionName.PRIVATE_DOCUMENTS];

export const PickArrayOfDto: FC<PickArrayOfDtoProps> = ({
  label,
  required,
  collections,
  createEntityTypes,
  fieldName,
  disabled,
  multiple = false,
  excludeIds,
  filter,
  isRequired,
  isHighlight,
  createEntityCategories,
}) => {
  const sjDb = useSJDatabase();
  const modals = useModalsStore();
  const [isSelectEntitiesModalOpen, setIsSelectEntitiesModalOpen] = useState(false);
  const { control, setValue } = useFormContext();

  const watchedValue = useWatch({ control, name: fieldName });

  const clearField = useCallback(
    (field: string) => {
      setValue(field, undefined, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue],
  );

  const options = useMemo(() => {
    return collections
      .map(
        (collection) =>
          sjDb.collections[collection]
            ?.findMany((item) => {
              const typeItem = item as PickArrayDtoItem;

              const isCommonFiltered = commonFilterCollection(item);
              const isSpecificFieldFiltered = !filter || filter(item, watchedValue, fieldName);
              const isExcludedDtoId = !excludeIds || !excludeIds.includes(item.id);

              const canBeCreatedByDtoType =
                !createEntityTypes || !typeItem.type || createEntityTypes.includes(typeItem.type);

              const itemCategory = getPickArrayItemCategory(typeItem);
              const canBeCreatedByDtoCategory = PickArrayItemWithCategory.includes(collection)
                ? !createEntityCategories ||
                  !itemCategory ||
                  createEntityCategories.includes(itemCategory)
                : true;

              return (
                isCommonFiltered &&
                isSpecificFieldFiltered &&
                isExcludedDtoId &&
                canBeCreatedByDtoType &&
                canBeCreatedByDtoCategory
              );
            })
            .map((item: PickArrayDtoItem) => ({
              value: item.id,
              content: {
                label: item.name || item.nickName || item.nickname || item.shortname || '',
              },
            })) ?? [],
      )
      .flat();
  }, [collections, sjDb, fieldName, filter, watchedValue, excludeIds, createEntityTypes]);

  const currentLabels = useMemo(() => {
    return options
      .filter((option) =>
        Array.isArray(watchedValue)
          ? watchedValue.includes(option.value)
          : option.value === watchedValue,
      )
      .map((option, i) => (
        <span className="chip" key={i}>
          {option.content.label}
        </span>
      ));
  }, [options, watchedValue]);

  const handleOpen = ({ onChange }: ControllerRenderProps<FieldValues, string>) => {
    setIsSelectEntitiesModalOpen(true);

    modals.openModal({
      type: 'SelectEntitiesModal',
      value: watchedValue,
      multiple,
      colNames: collections,
      title: label,
      createEntityTypes,
      createEntityCategories,
      excludeIds,
      filter,
      onSelect: onChange,
      onClose: () => {
        setIsSelectEntitiesModalOpen(false);
      },
    });
  };

  const handleReset: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      clearField(fieldName);
    },
    [clearField, fieldName],
  );

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ required: isRequired ? isRequired(fieldName, watchedValue) : required }}
      render={({ field, fieldState }) => (
        <>
          {ALLOWED_PICK_ARRAY_ENTITIES.some((entity) => collections.includes(entity)) ? (
            <FieldSelectView
              onFieldClick={() => handleOpen(field)}
              onClickReset={handleReset}
              label={label}
              selectedView={currentLabels}
              open={isSelectEntitiesModalOpen}
              disabled={disabled}
            />
          ) : (
            <Select
              name={`${field.name}__ui`}
              value={field.value ?? null}
              onChange={field.onChange}
              options={options}
              label={label}
              required={Boolean(required)}
              status={fieldState.error ? 'error' : 'default'}
              errorText={fieldState.error?.message}
              multiple={multiple}
              disabled={disabled}
              className={cn({ highlight: isHighlight && !fieldState.isDirty })}
            />
          )}
        </>
      )}
    />
  );
};
